// Core
import React from 'react'

// Library
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'

// Hooks
import { useHistory } from 'react-router-dom'

// Components
import StepProgress from 'components/Onboarding/StepProgress/StepProgress'
import BulletList from 'components/Onboarding/BulletList/BulletList'
import Button from 'components/Onboarding/Button/Button'
import PermissionDisclaimer from 'components/Onboarding/PermissionDisclaimer'

// Form Components
import TextInput from 'components/Onboarding/FormFields/TextInput'
import TelInput from 'components/Onboarding/FormFields/TelInput'

// Hooks
import { useAppDispatch, useAppSelector } from 'state-manager/store'

// Actions
import { updateUser } from 'state-manager/actions/user'
import { setCurrentSubscription } from 'state-manager/actions/subscriptions'

// Helpers
import { hasError } from 'helpers/field-has-errors'

// Constants
import { companyFormFields } from 'pages/Onboarding/Signup/constants'
import { PHONE_NUMBER } from 'constants/regex'

// Assets
import { ReactComponent as Logo } from 'assets/icons/logo_clicktech.svg'

// Styles
import classes from 'pages/Onboarding/Onboarding.module.scss'

// Routes
import routes from 'routes'

// Utils
import {sendGTMEvent} from 'utils/gtm/sendGTMEvent'

const validationSchema = (companyRequired: boolean = false) =>
  Yup.object({
    companyName: companyRequired
      ? Yup.string().required('Company Name is a required field')
      : Yup.string().notRequired(),
    phoneNumber: Yup.string()
      .notRequired()
      .test(
        'is-valid-phone',
        'Phone number is invalid',
        function (value) {
          return (!value) ? true : PHONE_NUMBER.test(value)
        }
      ),
    countryId: Yup.number().notRequired(),
  })

const CompanyDetails = () => {
  const { configCountries } = useAppSelector((state) => ({
    configCountries: state.countries.data,
  }))
  const dispatch = useAppDispatch()
  const history = useHistory()
  const onboaringInfoString = localStorage.getItem('onboarding')
  const onboardingInfo =
    onboaringInfoString != null ? JSON.parse(onboaringInfoString) : {}
  const businessType = onboardingInfo?.business_type
  const { firstName, lastName} = useAppSelector(
    (state) => state.userData
  )
  const fullUserName = [firstName, lastName].filter(Boolean).join(' ').trim()

  const isFreelance = businessType === 'freelancer'

  const formik = useFormik({
    initialValues: {
      companyName: '',
      phoneNumber: '',
      countryId: 44,
    },

    validationSchema: validationSchema(!isFreelance),

    onSubmit: (values) => {
      const { companyName, phoneNumber, countryId } = values

      const cId = configCountries.find(
        (item) => item.dial_code === `+${countryId}`
      )?.id
      const pNumber = phoneNumber
        .replace(/\D/g, '')
        .substring(countryId?.toString().length)

      const { business_type: businessType, priorities } = onboardingInfo

      onboardingInfo['company_name'] = companyName?.trim() || fullUserName
      onboardingInfo['phone_number'] = pNumber
      onboardingInfo['country_id'] = cId

      localStorage.setItem('onboarding', JSON.stringify(onboardingInfo))

      const updateUserData = {
        companyName: companyName?.trim() || fullUserName,
        phoneNumber: pNumber || null,
        countryId: cId,
        businessType,
        priorities,
      }

      dispatch(updateUser(updateUserData))

      sendGTMEvent({
        event: 'companyDetailsComplete',
        phone: pNumber ? 'TRUE' : 'FALSE'
      })

      const preselectedPlanId = window.localStorage.getItem(
        'preselectedPlanId'
      )

      if (preselectedPlanId) {
        dispatch(setCurrentSubscription({ planId: preselectedPlanId }, routes.onboardingConnect))
        return
      }

      history.push(routes.onboardingUsage)
    },
  })

  const companyNameHasError = hasError(
    'companyName',
    formik.touched,
    formik.errors
  )
  const phoneNumberHasError = hasError(
    'phoneNumber',
    formik.touched,
    formik.errors
  )
  const isSubmitDisabled = !formik.isValid

  return (
    <div className={clsx(classes.stepContainer)}>
      <div className={clsx(classes.stepAction)}>
        <div className={clsx(classes.stepSection, 'mt-2', 'mt-xl-up-5')}>
          <div className={clsx(classes.logo)}>
            <Logo />
          </div>
        </div>
        <div className={clsx(classes.stepSection)}>
          <div className={clsx(classes.stepProgress)}>
            <StepProgress step={1} label="account" theme="complete" />
            <StepProgress step={2} label="profile" theme="active" />
            <StepProgress
              step={3}
              label="usage"
              theme="pending"
              className="d-none-sm"
            />
            <StepProgress
              step={4}
              label="connect"
              theme="pending"
              className="d-none-sm"
            />
          </div>
        </div>{' '}
        <div className={clsx(classes.stepSection, 'mt-2')}>
          <h1 className={clsx(classes.almostThere, 'mt-3')}>Almost there!</h1>
        </div>
        <div className={clsx(classes.stepSection)}>
          <h2 className={clsx(classes.subheader)}>
            Just a few more details to personalize your experience
          </h2>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div
            className={clsx(classes.stepSection, 'mt-2', 'mt-xl-up-4', 'mb-4')}>
            <div className="form-group mt-0">
              <TextInput
                label={`Company Name${isFreelance ? ' (optional)' : ''}`}
                suffix={!isFreelance ? 'Required' : ''}
                name={companyFormFields.companyName}
                value={formik.values.companyName}
                errorMsg={companyNameHasError && formik.errors.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Your company name"
                dataCy="companyName"
              />
            </div>
            <div className="form-group mt-4">
              <TelInput
                country="gb"
                label="Phone Number (optional)"
                name={companyFormFields.phoneNumber}
                value={formik.values.phoneNumber}
                errorMsg={phoneNumberHasError && formik.errors.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Your phone number"
                dataCy="phoneNumber"
                setFieldValue={formik.setFieldValue}
              />
            </div>
          </div>
          <div className={clsx(classes.stepSection, 'mt-4', 'mb-4')}>
            <div className={clsx(classes.companyActions)}>
              <Button
                type="button"
                title="Back"
                inverted
                dataCy="back"
                className={clsx('m-px')}
                onClick={() => history.push(routes.onboardingProfilePriorities)}
              />
              <Button
                type="submit"
                disabled={isSubmitDisabled}
                title="Continue"
                dataCy="submit"
              />
            </div>
          </div>
        </form>
      </div>
      <div className={clsx(classes.infoContainer)}>
        <div className={clsx(classes.infoSection)}>
          <h2 className="fw-semibold">Your data is safe with us</h2>
        </div>
        <div className={clsx(classes.infoSection, 'mt-2')}>
          <PermissionDisclaimer quote="We'll never make any changes to your accounts without your explicit permission. You can revoke access at any time." />
        </div>
        <div className={clsx(classes.infoSection, 'mt-5')}>
          <BulletList
            features={[
              'We respect your privacy',
              'EDEE is GDPR compliant',
              'We use data encryption for your peace of mind',
            ]}
          />
        </div>
      </div>
    </div>
  )
}

export default CompanyDetails
